import React, {useState, useEffect, useRef} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";
import SendEmailModal from './shared/SendEmailModal/SendEmailModal';
import {formatSentAt} from './shared/SendEmailModal/formatSentAt';
import {downloadCsv, toUtcIso} from './shared/downloadCsv';

const EgocentricWaitlistRequestsTable = () => {
    const [requestsData, setRequestsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sendingId, setSendingId] = useState(null);
    const [sendError, setSendError] = useState('');
    const [showSendAll, setShowSendAll] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [downloadingCsv, setDownloadingCsv] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const isInitialLoad = useRef(true);

    useEffect(() => {
        const fetchRequestsData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("prismax_admin_token");
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/admin/get-marketing-egocentric-waitlist-requests?page=${currentPage}&page_size=${itemsPerPage}`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch egocentric waitlist requests');
                }
                const data = await response.json();
                if (data.success && data.data) {
                    setRequestsData(data.data);
                    setTotalPages(data.total_pages || 1);
                } else {
                    throw new Error('Invalid data format');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        };

        fetchRequestsData();
    }, [currentPage, reloadKey]);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const sendEmail = async (rowId) => {
        setSendingId(rowId);
        setSendError('');
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/admin/marketing-egocentric-waitlist-requests/${rowId}/send-email`,
                {
                    method: 'POST',
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.msg || 'Failed to send email');
            }
            setReloadKey((key) => key + 1);
        } catch (err) {
            setSendError(err.message);
        } finally {
            setSendingId(null);
        }
    };

    const downloadAllCsv = async () => {
        setDownloadingCsv(true);
        setSendError('');
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const allRows = [];
            let page = 1;
            let pages = 1;
            do {
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/admin/get-marketing-egocentric-waitlist-requests?page=${page}&page_size=500`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch egocentric waitlist requests');
                }
                const data = await response.json();
                if (!data.success || !data.data) {
                    throw new Error('Invalid data format');
                }
                allRows.push(...data.data);
                pages = data.total_pages || 1;
                page += 1;
            } while (page <= pages);
            downloadCsv('egocentric-waitlist', ['Request ID', 'Email', 'Name', 'Region', 'Setup', 'Tasks', 'Hours/Week', 'Created At', 'IP Address', 'Location', 'User Email Sent At'], allRows.map((item) => [
                item.egocentric_waitlist_request_id,
                item.email,
                item.name,
                item.region,
                item.setup,
                item.tasks,
                item.hours_per_week,
                toUtcIso(item.created_at),
                item.ip_address,
                item.location,
                toUtcIso(item.user_email_sent_at),
            ]));
        } catch (err) {
            setSendError(err.message);
        } finally {
            setDownloadingCsv(false);
        }
    };

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading egocentric waitlist requests...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeaderRow}>
                <h2 className={styles.tableTitle}>Egocentric Waitlist</h2>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.csvButton} disabled={downloadingCsv} onClick={downloadAllCsv}>
                        {downloadingCsv ? 'Preparing…' : 'Download CSV'}
                    </button>
                    <button type="button" className={styles.addButton} onClick={() => setShowSendAll(true)}>
                        Send email to all
                    </button>
                </div>
            </div>
            {sendError ? <div role="alert" className={styles.actionError}>{sendError}</div> : null}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Request ID</th>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Region</th>
                        <th>Setup</th>
                        <th>Tasks</th>
                        <th>Hours/Week</th>
                        <th>Created At</th>
                        <th>IP Address</th>
                        <th>Location</th>
                        <th>Send Email</th>
                    </tr>
                    </thead>
                    <tbody>
                    {requestsData.length > 0 ? (
                        requestsData.map((item) => (
                            <tr key={item.egocentric_waitlist_request_id}>
                                <td>{item.egocentric_waitlist_request_id}</td>
                                <td>{item.email}</td>
                                <td>{item.name}</td>
                                <td>{item.region}</td>
                                <td>{item.setup}</td>
                                <td>{item.tasks}</td>
                                <td>{item.hours_per_week}</td>
                                <td>{new Date(item.created_at).toLocaleString()}</td>
                                <td>{item.ip_address}</td>
                                <td>{item.location}</td>
                                <td>
                                    {item.user_email_sent_at ? (
                                        <span className={styles.sentText}>Sent {formatSentAt(item.user_email_sent_at)}</span>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.inlineSubmitButton}
                                            disabled={sendingId !== null}
                                            onClick={() => sendEmail(item.egocentric_waitlist_request_id)}
                                        >
                                            {sendingId === item.egocentric_waitlist_request_id ? 'Sending…' : 'Send email'}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="11" className={styles.noData}>
                                No egocentric waitlist requests found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {requestsData.length > 0 && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            // Show limited page numbers with ellipsis for better UX
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>

                </div>
            )}

            {showSendAll && (
                <SendEmailModal
                    title="Send email to all — Egocentric Waitlist"
                    adminToken={localStorage.getItem("prismax_admin_token")}
                    recipientsPath="/api/admin/marketing-egocentric-waitlist-requests/email-recipients"
                    sendPath="/api/admin/marketing-egocentric-waitlist-requests/send-email-bulk"
                    onClose={() => setShowSendAll(false)}
                    onSent={() => setReloadKey((key) => key + 1)}
                />
            )}
        </div>
    );
};

export default EgocentricWaitlistRequestsTable;
